import type { SavedNode } from '@/components/canvas/utils/coord-converter'
import type { SerializedEdge } from '@/lib/api/canvas-types'

/**
 * Last-resort local copy of canvas content the server has not accepted yet.
 *
 * A tab that closes gets one chance to push its unsaved work, and that chance
 * is unreliable: `fetch(..., { keepalive: true })` silently rejects bodies over
 * 64 KiB, and a canvas passes 64 KiB long before it feels large. So before
 * trying to send, we write the content here. The next load of that page finds
 * it and replays it, which turns "the browser dropped my last edits" into "the
 * page came back the way I left it".
 *
 * localStorage, not IndexedDB, on purpose: an IndexedDB transaction opened in a
 * `beforeunload` handler is asynchronous and usually aborted when the document
 * goes away, whereas `localStorage.setItem` has already happened when it
 * returns. The price is the ~5 MB origin quota, which we degrade against below.
 */

const PENDING_PREFIX = 'genie:pending-save:'
/** Entries older than this are assumed superseded and dropped on sight */
const PENDING_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
/**
 * Stop storing the base alongside the pending content past this size. The base
 * only improves conflict resolution; the pending content is the work itself.
 */
const BASE_BUDGET_BYTES = 2 * 1024 * 1024

export interface PendingSaveContent {
  nodes: SavedNode[]
  edges: SerializedEdge[]
}

export interface PendingSaveEntry {
  pageUuid: string
  /** Revision the pending content is based on */
  revision: number
  /** Content this tab failed to send */
  pending: PendingSaveContent
  /**
   * Content the server was known to hold at `revision`. Present unless it was
   * dropped to fit the quota, in which case a replay treats every pending item
   * as locally changed.
   */
  base?: PendingSaveContent
  /** Page config (viewport) that was riding along with the save */
  config?: Record<string, unknown>
  savedAt: number
}

function keyFor(pageUuid: string): string {
  return `${PENDING_PREFIX}${pageUuid}`
}

/**
 * Store content that has not reached the server. Synchronous, so it is safe to
 * call from `beforeunload`.
 *
 * Falls back to storing the pending content without its base, then gives up
 * with a warning, rather than throwing out of an unload handler.
 */
export function writePendingSave(entry: PendingSaveEntry): boolean {
  const key = keyFor(entry.pageUuid)
  const withBase: PendingSaveEntry = { ...entry, savedAt: Date.now() }
  const baseSize = withBase.base ? JSON.stringify(withBase.base).length : 0
  const candidates: PendingSaveEntry[] =
    baseSize > BASE_BUDGET_BYTES || !withBase.base
      ? [{ ...withBase, base: undefined }]
      : [withBase, { ...withBase, base: undefined }]

  for (const candidate of candidates) {
    try {
      localStorage.setItem(key, JSON.stringify(candidate))
      return true
    } catch {
      // Quota or private-mode failure — try the smaller shape, then give up
    }
  }
  console.warn('[Save] Unsent canvas content did not fit in local storage')
  return false
}

/** Read the pending entry for a page, dropping it if it is stale or corrupt */
export function readPendingSave(pageUuid: string): PendingSaveEntry | null {
  const key = keyFor(pageUuid)
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const entry = JSON.parse(raw) as PendingSaveEntry
    if (
      !entry ||
      typeof entry.revision !== 'number' ||
      !Array.isArray(entry.pending?.nodes) ||
      !Array.isArray(entry.pending?.edges) ||
      Date.now() - entry.savedAt > PENDING_MAX_AGE_MS
    ) {
      localStorage.removeItem(key)
      return null
    }
    return entry
  } catch {
    try {
      localStorage.removeItem(key)
    } catch {
      // storage unavailable
    }
    return null
  }
}

/** Drop the pending entry for a page (its content reached the server) */
export function clearPendingSave(pageUuid: string): void {
  try {
    localStorage.removeItem(keyFor(pageUuid))
  } catch {
    // storage unavailable — nothing to clean up
  }
}

/** Remove pending entries left behind by pages nobody reopened */
export function cleanStalePendingSaves(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith(PENDING_PREFIX)) continue
      try {
        const entry = JSON.parse(localStorage.getItem(key) ?? '') as PendingSaveEntry
        if (Date.now() - entry.savedAt > PENDING_MAX_AGE_MS) localStorage.removeItem(key)
      } catch {
        localStorage.removeItem(key)
      }
    }
  } catch {
    // storage unavailable
  }
}
